import { useState } from 'react';

// MUI
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Custom components
import PreBuildReports from './preBuildReports';
import CustomReports from './customReports';
import SearchInput from 'components/Form/TextField/SearchField.cmp';

// Other Libraries
import { Card, CardsContainer, Header } from './styles';

// Icons
import GridBuilderIcon from 'components/Icons/templateStepIcons2/gridBuilderIcon';
import CustomDashboardIcon from 'components/Icons/sidebarIcons/customdashboardIcon';
import xeniaApi from 'api/index';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import { useCreateDashboard } from 'pages/Dashboards/hooks/useDashboard';

interface GridReportProps {
  listHeight?: number;
}

const createDashboardPayload = {
  title: 'Untitled Dashboard',
};

const ReportingHome: React.FC<GridReportProps> = ({ listHeight = 500 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeSearch = (e) => {
    const searchInputValue = e.target.value;
    setSearchQuery(searchInputValue);
  };

  const { navigateWithWorkspaceUrl } = useWorkspaceHook();

  const createNewGridReport = async () => {
    setIsLoading(true);
    const data = await xeniaApi.createGridReport();
    setIsLoading(false);
    return data.id;
  };
  const handleCreateReport = async () => {
    const gridReportId = await createNewGridReport();
    if (gridReportId) {
      navigateWithWorkspaceUrl(`/grid-report/${gridReportId}`);
    }
  };

  const { createDashboardQuery } = useCreateDashboard();
  const handleAddDashboard = () => {
    if (createDashboardQuery.isLoading) return;
    createDashboardQuery.mutate(createDashboardPayload, {
      onSuccess: (data) => {
        navigateWithWorkspaceUrl(`/dashboards/${data.id}`);
      },
    });
  };
  return (
    <Box p={2}>
      <Header sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {' '}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '22px',
              color: '#000000',
            }}
          >
            Reporting Home
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#000000',
            }}
          >
            View performance across teams and build custom reports.
          </Typography>
        </Box>
        <SearchInput
          fieldProps={{
            value: searchQuery,
            onChange: (e) => handleChangeSearch(e),
          }}
          sx={{
            maxWidth: { sm: 'calc(100vw - 140px)', md: 250 },
            width: { sm: '100%', md: 350 },
            '& .MuiInputBase-adornedStart': {
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #E0E0E0',
            },
            '& .MuiOutlinedInput-input': {
              background: '#fff',
            },
          }}
        />
      </Header>

      <CardsContainer>
        <Card onClick={handleCreateReport}>
          <Box className="icon">
            <GridBuilderIcon />
          </Box>
          <Typography className="title">Create Grid Report</Typography>
          <Typography className="content">
            Track submissions or project completion in a table format.
          </Typography>
        </Card>
        <Card onClick={handleAddDashboard}>
          <Box className="icon">
            <CustomDashboardIcon />
          </Box>
          <Typography className="title">Create Custom Dashboard</Typography>
          <Typography className="content">
            Visualize key metrics with charts and widgets.
          </Typography>
        </Card>
      </CardsContainer>

      <Box sx={{ pt: 2, pb: 2, mb: 3 }}>
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          square
          sx={{
            background: 'transparent',
            boxShadow: 'none',
            borderRadius: 2,
            '&:before': { display: 'none' },
            mb: 2,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#222' }} />}
            sx={{
              minHeight: 48,
              flexDirection: 'row-reverse',
              padding: 0,
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: '18px', color: '#222' }}
            >
              Pre-Built Reports
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PreBuildReports searchQuery={searchQuery} />
          </AccordionDetails>
        </Accordion>

        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          square
          sx={{
            background: 'transparent',
            boxShadow: 'none',
            borderRadius: 2,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#222' }} />}
            sx={{
              minHeight: 48,
              flexDirection: 'row-reverse',
              padding: 0,
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: '18px', color: '#222' }}
            >
              Reports
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <CustomReports searchQuery={searchQuery} />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default ReportingHome;
